import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  CheckCircle,
  Error,
  Sms,
  Schedule,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Mensagem } from '../../services/mensagemService';

interface SmsViewMsgProps {
  open: boolean;
  onClose: () => void;
  mensagem: Mensagem | null;
}

const SmsViewMsg: React.FC<SmsViewMsgProps> = ({
  open,
  onClose,
  mensagem,
}) => {
  const formatSmsDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const isYesterday = date.toDateString() === new Date(now.getTime() - 86400000).toDateString();
      
      if (isToday) {
        return format(date, 'HH:mm', { locale: ptBR });
      } else if (isYesterday) {
        return `Ontem ${format(date, 'HH:mm', { locale: ptBR })}`;
      } else {
        return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
      }
    } catch {
      return dateString;
    }
  };

  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      enviado: 'Enviado',
      aguardando_envio: 'Aguardando Envio',
      falhou: 'Falhou',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enviado':
        return 'success';
      case 'aguardando_envio':
        return 'warning';
      case 'falhou':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'enviado':
        return <CheckCircle fontSize="small" />;
      case 'aguardando_envio':
        return <Schedule fontSize="small" />;
      case 'falhou':
        return <Error fontSize="small" />;
      default:
        return null;
    }
  };

  if (!mensagem || mensagem.tipo !== 'sms') {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header do SMS */}
      <Box
        sx={{
          bgcolor: '#1976d2',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sms />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {mensagem.cliente?.nome || 'SMS'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {mensagem.destino}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Área de Mensagens */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'auto',
          bgcolor: '#f5f5f5',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Mensagem SMS */}
        <Box
          sx={{
            alignSelf: 'flex-end',
            maxWidth: '75%',
            bgcolor: '#ffffff',
            borderRadius: '16px 16px 4px 16px',
            p: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            position: 'relative',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#000',
              fontSize: '0.95rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mb: 1,
            }}
          >
            {mensagem.conteudo}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontSize: '0.7rem',
              }}
            >
              {formatSmsDate(mensagem.data_envio || mensagem.createdAt)}
            </Typography>
            {mensagem.status === 'enviado' && (
              <CheckCircle
                sx={{
                  fontSize: '0.9rem',
                  color: '#4caf50',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Informações adicionais (se houver erro) */}
        {mensagem.erro_mensagem && (
          <Box
            sx={{
              alignSelf: 'center',
              maxWidth: '85%',
              bgcolor: 'rgba(244, 67, 54, 0.1)',
              borderRadius: '8px',
              p: 2,
              border: '1px solid rgba(244, 67, 54, 0.3)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#d32f2f',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Error sx={{ fontSize: '1rem' }} />
              Erro: {mensagem.erro_mensagem}
            </Typography>
          </Box>
        )}

        {/* Informações da Mensagem */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            bgcolor: 'white',
            borderRadius: 2,
            mt: 'auto',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getStatusIcon(mensagem.status)}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatStatus(mensagem.status)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                Tentativas
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {mensagem.tentativas}
              </Typography>
            </Grid>
            {mensagem.usuario && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                  Enviado por
                </Typography>
                <Typography variant="body2">
                  {mensagem.usuario.nome} ({mensagem.usuario.usuario})
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    </Dialog>
  );
};

export default SmsViewMsg;

